import type { ChipTone } from "@outpave/ds";
import PaymentStatus from "enums/PaymentStatus";
import WorkflowRequestStatus from "enums/WorkflowRequestStatus";

/**
 * What a payment shows as its status: the enum, plus three states that live on
 * the workflow request rather than on the payment.
 */
export type PaymentDisplayStatus =
  | PaymentStatus
  | "WORKFLOW_PENDING"
  | "WORKFLOW_REJECTED"
  | "PENDING_PAYMENT_ACCOUNT";

/**
 * The one place a payment status becomes a label and a tone.
 *
 * The table's chip and the details summary each had their own copy and they
 * disagreed: PENDING_PAYMENT printed "Scheduled Payment" on the table and
 * "Pending Payment" in the summary, and the chip's label was not a key in
 * either locale file, so it was English in every tenant. Same shape as
 * RenderUserStatus (OUT-1407) and RenderRelationship (OUT-1408).
 */
const PAYMENT_STATUS_LABEL_KEY: Record<PaymentDisplayStatus, string> = {
  [PaymentStatus.DRAFT]: "Draft",
  [PaymentStatus.PENDING]: "Pending",
  [PaymentStatus.PENDING_PAYMENT]: "Scheduled",
  [PaymentStatus.PROCESSING_PAYMENT]: "Processing",
  [PaymentStatus.COMPLETE]: "Complete",
  [PaymentStatus.CANCELED]: "Canceled",
  [PaymentStatus.PAYMENT_FAILED]: "Failed",
  [PaymentStatus.INSUFFICIENT_FUNDS]: "Insufficient Funds",
  [PaymentStatus.RETURNED]: "Returned",
  WORKFLOW_PENDING: "Approval",
  WORKFLOW_REJECTED: "Rejected",
  PENDING_PAYMENT_ACCOUNT: "Pending Bank",
};

/**
 * Money that did not arrive is `negative`, money on its way is `info`, and a
 * payment blocked on something a person must supply is `warning`. Six of these
 * used to share one MUI `warning`, which is a component with no tones rather
 * than a deliberate palette.
 */
const PAYMENT_STATUS_TONE: Record<PaymentDisplayStatus, ChipTone> = {
  [PaymentStatus.DRAFT]: "neutral",
  [PaymentStatus.PENDING]: "info",
  [PaymentStatus.PENDING_PAYMENT]: "info",
  [PaymentStatus.PROCESSING_PAYMENT]: "info",
  [PaymentStatus.COMPLETE]: "positive",
  [PaymentStatus.CANCELED]: "warning",
  [PaymentStatus.PAYMENT_FAILED]: "negative",
  [PaymentStatus.INSUFFICIENT_FUNDS]: "negative",
  [PaymentStatus.RETURNED]: "negative",
  WORKFLOW_PENDING: "info",
  WORKFLOW_REJECTED: "negative",
  PENDING_PAYMENT_ACCOUNT: "warning",
};

const WORKFLOW_STATUS_LABEL_KEY: Record<WorkflowRequestStatus, string> = {
  [WorkflowRequestStatus.PENDING]: "Pending",
  [WorkflowRequestStatus.APPROVED]: "Approved",
  [WorkflowRequestStatus.REJECTED]: "Rejected",
  [WorkflowRequestStatus.CANCELED]: "Canceled",
};

/**
 * A PENDING payment shows what it is actually waiting on, which is the
 * approval workflow when one is open and the recipient's bank otherwise.
 */
export const paymentDisplayStatus = (
  // Nullable, because `Payment.status` is: an incomplete row resolves to
  // `undefined` here and falls through to the Unknown Status label and the
  // neutral tone, rather than the caller having to guard first.
  paymentStatus: PaymentStatus | string | null | undefined,
  workflowStatus?: WorkflowRequestStatus,
): PaymentDisplayStatus | undefined => {
  if (!paymentStatus) return undefined;

  if (paymentStatus !== PaymentStatus.PENDING) {
    return paymentStatus in PAYMENT_STATUS_LABEL_KEY
      ? (paymentStatus as PaymentDisplayStatus)
      : undefined;
  }

  if (
    workflowStatus === WorkflowRequestStatus.PENDING ||
    workflowStatus === WorkflowRequestStatus.REJECTED
  ) {
    return `WORKFLOW_${workflowStatus}` as PaymentDisplayStatus;
  }

  // Only an absent or approved workflow means the payment is waiting on the
  // recipient's bank. Anything else (a canceled request) is still just pending.
  return !workflowStatus || workflowStatus === WorkflowRequestStatus.APPROVED
    ? "PENDING_PAYMENT_ACCOUNT"
    : PaymentStatus.PENDING;
};

/** A translation KEY, so the caller owns the `t()` and no stored value reaches one. */
export const paymentStatusLabelKey = (
  status: PaymentDisplayStatus | string | null | undefined,
): string =>
  (status && PAYMENT_STATUS_LABEL_KEY[status as PaymentDisplayStatus]) ||
  "Unknown Status";

/** Fall-through is `neutral`: a status this map has not heard of is unknown, not failed. */
export const paymentStatusTone = (
  status: PaymentDisplayStatus | string | null | undefined,
): ChipTone =>
  (status && PAYMENT_STATUS_TONE[status as PaymentDisplayStatus]) || "neutral";

export const workflowStatusLabelKey = (
  status: WorkflowRequestStatus | string | null | undefined,
): string | undefined =>
  status
    ? WORKFLOW_STATUS_LABEL_KEY[status as WorkflowRequestStatus]
    : undefined;
